import { Link, useLocation } from "wouter";
import { ArrowRight, Compass, House, MessageSquareText, Wrench } from "lucide-react";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { FeatureExplorer } from "@/components/feature-explorer";
import { FaqList, FinalBand } from "@/components/marketing/sections";
import { PricingCards } from "@/components/marketing/pricing-cards";
import { StartFreeButton } from "@/components/marketing/cta";
import {
  HomeV2Founder,
  HomeV2Proof,
  HomeV2Setup,
} from "@/components/marketing/home-v2/story-sections";
import { COMPARISON_ROWS, HOME_FAQ } from "@/data/core-pages";
import { track } from "@/lib/marketing/analytics";
import { signupHref } from "@/lib/marketing/links";

const comparisonLabels = [
  { icon: MessageSquareText, label: "Member conversations" },
  { icon: House, label: "Room and property operations" },
  { icon: Wrench, label: "Maintenance and vendors" },
];

export default function HomeV3() {
  const [, navigate] = useLocation();

  const openSignup = () => {
    track("cta_clicked", { cta_id: "home_v3_feature_explorer_start_free", location: "home_v3_features" });
    navigate(signupHref());
  };

  return (
    <div className="flex min-h-[100dvh] flex-col bg-[#fffdf8] text-[#183347]" data-testid="page-home-v3">
      <a
        href="#main-content"
        className="sr-only z-50 rounded-md bg-[#fffdf8] px-4 py-3 font-semibold text-brand shadow focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        Skip to main content
      </a>
      <PublicHeader />
      <main id="main-content" className="flex-grow outline-none" tabIndex={-1}>
        <section
          className="relative isolate overflow-hidden bg-brand-dark px-4 pb-16 pt-28 text-white md:pb-24 md:pt-36"
          aria-labelledby="home-v3-title"
          data-testid="section-hero"
        >
          <div
            className="pointer-events-none absolute inset-0 -z-10 opacity-60"
            aria-hidden="true"
            style={{
              backgroundImage:
                "radial-gradient(ellipse at 82% 18%, rgba(226,157,103,.25), transparent 34%), linear-gradient(135deg, transparent 49%, rgba(255,255,255,.035) 49.2%, transparent 49.5%)",
            }}
          />
          <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-[.95fr_1.05fr] lg:gap-14">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[.18em] text-brand-accent">
                A better operating rhythm for shared houses
              </p>
              <h1
                id="home-v3-title"
                className="mt-5 max-w-[15ch] text-[2.55rem] font-bold leading-[1.04] tracking-[-.045em] text-[#fff9ef] sm:text-5xl lg:text-[3.55rem]"
                data-testid="text-page-heading"
              >
                The all-in-one platform built for co-living hosts
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-[#dbe5e4]">
                PadPulse brings member conversations, house operations, maintenance, and property finances into one
                workspace—whether your rooms are on PadSplit, off PadSplit, or a mix of both.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <StartFreeButton
                  ctaId="home_v3_hero_start_free"
                  location="home_v3_hero"
                  className="min-h-12 justify-center px-7"
                />
              </div>
              <p className="mt-4 text-sm leading-6 text-[#b9cacb]">
                Connect your PadSplit host account, then keep the rest of your operation in view.
              </p>
              <p className="mt-2 text-xs leading-5 text-[#9fb5b7]">
                PadPulse is independent software. PadSplit connection is optional for hosts managing rooms directly.
              </p>
            </div>

            <figure className="relative mx-auto w-full max-w-[620px] lg:mr-0">
              <div className="absolute -inset-3 -rotate-2 rounded-[2rem] border border-white/10 bg-white/[.04]" aria-hidden="true" />
              <img
                src="/images/home-v2/coliving.webp"
                width={1440}
                height={1080}
                loading="eager"
                decoding="async"
                alt="Illustrated cutaway of a shared co-living home with private rooms and shared spaces"
                className="relative aspect-[4/3] w-full rounded-[1.5rem] border border-white/15 object-cover shadow-2xl shadow-black/25"
              />
              <figcaption className="mt-3 flex items-start gap-2 px-1 text-xs leading-5 text-[#bdcdcc]">
                <span className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full bg-brand-accent" aria-hidden="true" />
                An illustration of a co-living house—not a product screenshot.
              </figcaption>
            </figure>
          </div>
        </section>

        <HomeV2Proof includeSavings />

        <section className="bg-[#fffdf8] px-4 py-16 md:py-20" aria-labelledby="home-v3-tools-title" data-testid="section-toolkit">
          <div className="mx-auto max-w-6xl">
            <div className="mb-9 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div className="max-w-2xl">
                <p className="text-xs font-bold uppercase tracking-[.15em] text-brand">A working toolkit, not a patchwork</p>
                <h2 id="home-v3-tools-title" className="mt-3 text-3xl font-bold tracking-tight text-[#183347] md:text-4xl">
                  See how the pieces fit together.
                </h2>
              </div>
              <p className="max-w-lg text-sm leading-6 text-[#52636a]">
                Explore every feature tab with real app screenshots. The tour advances automatically;
                hover, use the keyboard, or pause it to inspect a workspace at your own pace.
              </p>
            </div>
            <FeatureExplorer previewMode="screenshots" onCtaClick={openSignup} />
            <p className="mt-4 text-xs leading-5 text-[#657276]">
              Every tab shows an actual PadPulse workspace with anonymized, illustrative demo data,
              not live operating results. Select a screenshot to enlarge it.
            </p>
          </div>
        </section>

        <section className="bg-[#e9eee9] px-4 py-16 md:py-20" aria-labelledby="home-v3-mixed-title" data-testid="section-on-off-padsplit">
          <div className="mx-auto grid max-w-5xl items-center gap-8 md:grid-cols-[1fr_auto] md:gap-12">
            <div>
              <p className="text-xs font-bold uppercase tracking-[.15em] text-brand">On PadSplit. Off PadSplit. Both.</p>
              <h2 id="home-v3-mixed-title" className="mt-3 max-w-2xl text-3xl font-bold leading-tight tracking-tight text-[#183347] md:text-4xl">
                Manage the whole house, even when rooms run differently.
              </h2>
              <p className="mt-4 max-w-2xl text-base leading-7 text-[#52636a]">
                Connect PadSplit rooms to bring member and room details into your workspace. For rooms you manage
                directly, use PadPulse tools for direct booking, booking requests, listings, operations, and finances.
                Keep both kinds of rooms under the same roof without treating them as the same channel.
              </p>
              <p className="mt-4 max-w-2xl text-sm leading-6 text-[#52636a]">
                Direct bookings can avoid PadSplit’s 10-day booking fee; the estimated savings are $250+ per booking
                versus that fee, depending on the booking.
              </p>
            </div>
            <div className="rounded-xl border border-[#cbd7ce] bg-[#fffdf8] px-7 py-6 text-center md:min-w-[15rem]" data-testid="stat-direct-booking-savings">
              <p className="font-mono text-3xl font-bold tracking-tight text-brand">$250+</p>
              <p className="mt-2 text-sm leading-5 text-[#52636a]">potential savings per direct booking vs. PadSplit fees</p>
            </div>
          </div>
        </section>

        <section className="bg-[#f6f3eb] px-4 py-14 md:py-20" aria-labelledby="home-v3-compare-title" data-testid="section-comparison">
          <div className="mx-auto max-w-5xl">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[.15em] text-brand">Work alongside PadSplit</p>
              <h2 id="home-v3-compare-title" className="mt-3 text-3xl font-bold tracking-tight text-[#183347] md:text-4xl">
                Keep the marketplace. Add a home base for the rest.
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-6 text-[#52636a]">
                PadPulse complements your PadSplit account; it does not replace PadSplit’s marketplace or host
                responsibilities.
              </p>
            </div>
            <div className="mt-8 hidden overflow-x-auto rounded-xl border border-[#deded5] bg-[#fffdf8] md:block">
              <table className="w-full text-left" data-testid="table-comparison">
                <thead>
                  <tr className="border-b border-[#e6e4dc] text-sm text-[#657276]">
                    <th scope="col" className="px-5 py-3 font-medium">Host job</th>
                    <th scope="col" className="px-5 py-3 font-medium">PadSplit</th>
                    <th scope="col" className="px-5 py-3 font-medium">PadPulse alongside it</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row) => (
                    <tr key={row.job} className="border-b border-[#efeee8] align-top last:border-b-0">
                      <th scope="row" className="px-5 py-4 font-semibold text-[#183347]">{row.job}</th>
                      <td className="px-5 py-4 text-sm leading-6 text-[#617074]">{row.padsplit}</td>
                      <td className="px-5 py-4 text-sm leading-6 font-medium text-[#344c50]">{row.padpulse}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 space-y-3 md:hidden" data-testid="list-comparison-mobile">
              {COMPARISON_ROWS.map((row, index) => {
                const Icon = comparisonLabels[index % comparisonLabels.length].icon;
                return (
                  <article key={row.job} className="min-w-0 rounded-xl border border-[#deded5] bg-[#fffdf8] p-4">
                    <div className="flex items-center gap-2 text-brand">
                      <Icon size={17} aria-hidden="true" />
                      <h3 className="font-semibold text-[#183347]">{row.job}</h3>
                    </div>
                    <p className="mt-3 text-sm leading-5 text-[#617074]"><span className="font-semibold">PadSplit:</span> {row.padsplit}</p>
                    <p className="mt-2 text-sm leading-5 text-[#344c50]"><span className="font-semibold">With PadPulse:</span> {row.padpulse}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <HomeV2Setup />

        <section className="bg-[#fffdf8] px-4 py-10 md:py-12" aria-label="Free research tools">
          <div className="mx-auto flex max-w-5xl flex-col gap-5 rounded-2xl border border-[#d8d8cf] bg-[#f6f3eb] p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
            <div className="flex items-start gap-4">
              <span className="rounded-xl bg-[#dce7dc] p-3 text-brand" aria-hidden="true"><Compass size={22} /></span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[.15em] text-brand">Start with the numbers</p>
                <h2 className="mt-2 text-xl font-bold text-[#183347]">Research tools are free to explore.</h2>
                <p className="mt-2 max-w-xl text-sm leading-6 text-[#52636a]">
                  Run a first-pass look at a market or property before you decide what to do next.
                </p>
              </div>
            </div>
            <Link
              href="/research"
              className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg px-4 font-semibold text-brand hover:bg-white/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              data-testid="link-home-v3-research"
            >
              Explore free research <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </section>

        <HomeV2Founder />

        <section className="overflow-hidden bg-[#edf0ed] px-4 py-14 md:py-20" aria-labelledby="home-v3-pricing-title" data-testid="section-home-pricing">
          <div className="mx-auto max-w-6xl px-0.5 sm:px-1">
            <p className="text-center text-xs font-bold uppercase tracking-[.15em] text-brand">Pricing that follows your operation</p>
            <h2 id="home-v3-pricing-title" className="mt-3 text-center text-3xl font-bold tracking-tight text-[#183347] md:text-4xl">
              Choose the plan that fits your next move.
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-sm leading-6 text-[#52636a]">
               Explore plans for Research, Host, and Property Manager accounts. AI reply drafting
              requires beta access; continuous Auto Draft additionally requires enabling the feature. Signup or payment
              alone does not grant access.
            </p>
            <div className="mt-9">
              <PricingCards location="home_v3_pricing" />
            </div>
            <div className="mx-auto mt-6 max-w-3xl rounded-xl border border-[#d4dcd4] bg-[#fffdf8] p-4 text-center text-sm leading-6 text-[#52636a]">
               Syncs with Mercury, with unlimited banking partner options coming in the near future.
               {" "}Mercury is supported now; broader banking connections are upcoming, not available today.
            </div>
          </div>
        </section>

        <section className="bg-[#fffdf8] px-4 py-14 md:py-20" aria-labelledby="home-v3-faq-title">
          <div className="mx-auto max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[.15em] text-brand">Good to know</p>
            <h2 id="home-v3-faq-title" className="mb-8 mt-3 text-3xl font-bold tracking-tight text-[#183347]">Questions, answered.</h2>
            <FaqList items={HOME_FAQ} testId="section-home-faq" />
          </div>
        </section>
        <FinalBand location="home_v3" />
      </main>
      <PublicFooter />
    </div>
  );
}
